import { useRef, useState } from "react";
import SignatureCanvas from "react-signature-canvas";
import { toast } from "sonner";
import { X, Eraser, ShieldCheck } from "lucide-react";
import api from "@/lib/api";
import { eur } from "@/lib/format";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function QuoteDecisionModal({ token, quote, action, onClose, onDone }) {
  const [step, setStep] = useState(0); // 0 intro, 1 code + signature
  const [sentTo, setSentTo] = useState("");
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [motif, setMotif] = useState("");
  const [cgv, setCgv] = useState(false);
  const [busy, setBusy] = useState(false);
  const [empty, setEmpty] = useState(true);
  const pad = useRef(null);
  const isSign = action === "sign";

  const requestCode = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/portal/${token}/quotes/${quote.id}/otp`, { action }); setSentTo(data.sent_to); setStep(1); toast.success(`Code envoyé à ${data.sent_to}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Impossible d'envoyer le code"); }
    finally { setBusy(false); }
  };
  const confirm = async () => {
    if (code.length !== 6) { toast.error("Saisissez le code à 6 chiffres"); return; }
    if (isSign && (!pad.current || pad.current.isEmpty())) { toast.error("Merci de signer dans le cadre"); return; }
    if (isSign && !cgv) { toast.error("Merci d'accepter les conditions"); return; }
    setBusy(true);
    try {
      if (isSign) {
        const signature = pad.current.getTrimmedCanvas().toDataURL("image/png");
        const { data } = await api.post(`/portal/${token}/quotes/${quote.id}/sign`, { code, signature, signed_by: name, accept_cgv: cgv });
        toast.success(`Devis ${quote.numero} signé${data.acompte_invoice ? ` — facture d'acompte ${data.acompte_invoice.numero} disponible` : ""}`);
      } else {
        await api.post(`/portal/${token}/quotes/${quote.id}/refuse`, { code, motif });
        toast.success("Votre décision a bien été transmise");
      }
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-anthracite/80 flex items-start md:items-center justify-center p-4 overflow-y-auto" data-testid="quote-decision-modal">
      <div className="bg-ivory w-full max-w-2xl rounded-rama-lg shadow-2xl my-6">
        <div className="flex items-start justify-between p-6 border-b border-black/10">
          <div>
            <div className="font-mono-caption text-bordeaux-700 mb-1">{isSign ? "Bon pour accord" : "Refuser le devis"}</div>
            <div className="font-display text-2xl">Devis {quote.numero} · {eur(quote.totals?.total_ttc)} TTC</div>
          </div>
          <button onClick={onClose} aria-label="Fermer" className="p-2 rounded hover:bg-black/5" data-testid="decision-close"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-5">
          {step === 0 && (
            <>
              <p className="text-sm text-ink-soft leading-relaxed">
                {isSign ? "Pour signer votre devis en toute sécurité, nous vous envoyons un code de confirmation à usage unique par email. Il vous sera demandé avec votre signature." : "Pour confirmer votre refus, un code de confirmation à usage unique vous sera envoyé par email."}
              </p>
              <div className="flex items-center gap-2 text-xs text-ink-soft"><ShieldCheck size={14} className="text-bordeaux-700" /> Code valable 10 minutes · 5 essais maximum · horodatage et adresse email conservés comme preuve.</div>
              <a href={`${API_BASE}/api/portal/${token}/pdf/devis/${quote.id}`} target="_blank" rel="noreferrer" className="text-sm text-bordeaux-700 underline">Relire le devis (PDF)</a>
              <button data-testid="decision-request-code" disabled={busy} onClick={requestCode} className="btn-primary w-full justify-center disabled:opacity-50">{busy ? "Envoi…" : "Recevoir mon code par email"}</button>
            </>
          )}
          {step === 1 && (
            <>
              <div className="text-sm text-ink-soft">Code envoyé à <b>{sentTo}</b>. <button onClick={requestCode} className="underline">Renvoyer</button></div>
              <label className="block"><span className="text-xs font-mono-caption text-ink-soft block mb-1.5">Code de confirmation</span>
                <input data-testid="decision-code" inputMode="numeric" maxLength={6} value={code} onChange={e => setCode(e.target.value.replace(/\D/g, ""))} className="w-full p-3 text-center text-2xl tracking-[0.5em] font-mono border border-black/15 rounded-rama-sm bg-white" placeholder="••••••" /></label>
              {isSign ? (
                <>
                  <label className="block"><span className="text-xs font-mono-caption text-ink-soft block mb-1.5">Nom et prénom du signataire</span>
                    <input data-testid="decision-name" value={name} onChange={e => setName(e.target.value)} className="w-full p-2.5 border border-black/15 rounded-rama-sm bg-white text-sm" /></label>
                  <div>
                    <div className="flex justify-between items-center mb-1.5"><span className="text-xs font-mono-caption text-ink-soft">Votre signature</span><button onClick={() => { pad.current?.clear(); setEmpty(true); }} className="text-xs text-ink-soft inline-flex items-center gap-1"><Eraser size={12} /> Effacer</button></div>
                    <div className="bg-white border border-black/15 rounded-rama-sm overflow-hidden" data-testid="decision-signature-pad">
                      <SignatureCanvas ref={pad} penColor="#1E1E1E" onEnd={() => setEmpty(false)} canvasProps={{ className: "w-full h-44", style: { width: "100%", height: "176px" } }} />
                    </div>
                    {empty && <div className="text-xs text-ink-soft mt-1">Signez au doigt ou à la souris.</div>}
                  </div>
                  <label className="flex items-start gap-2 text-sm"><input type="checkbox" data-testid="decision-cgv" checked={cgv} onChange={e => setCgv(e.target.checked)} className="mt-1" /> <span>J'accepte ce devis, ses conditions de règlement{quote.acompte_pct ? ` (acompte de ${quote.acompte_pct} % à la commande)` : ""} et je reconnais que cette signature électronique vaut bon pour accord.</span></label>
                </>
              ) : (
                <label className="block"><span className="text-xs font-mono-caption text-ink-soft block mb-1.5">Motif (optionnel, il nous aide à progresser)</span>
                  <textarea data-testid="decision-motif" rows={3} value={motif} onChange={e => setMotif(e.target.value)} className="w-full p-2.5 border border-black/15 rounded-rama-sm bg-white text-sm" /></label>
              )}
              <button data-testid="decision-confirm" disabled={busy} onClick={confirm} className="btn-primary w-full justify-center disabled:opacity-50">{busy ? "Vérification…" : isSign ? "Signer et accepter le devis" : "Confirmer le refus"}</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
